// Location: src/components/admin/analytics/AffiliateRewardsAnalytics.tsx
// Purpose: Affiliate rewards analytics dashboard showing performance, conversions, and earnings
// Scope: Display affiliate stats, traffic sources, campaign performance, and conversion metrics

import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
	Target,
	TrendingUp,
	Globe,
	Eye,
	Mail,
	Activity,
	ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmailLeadsCard } from './components/EmailLeadsCard';
import { SalesCard } from './components/SalesCard';
import { RecentConversionsCard } from './components/RecentConversionsCard';

interface AffiliateRewardsAnalyticsProps {
	className?: string;
}

export function AffiliateRewardsAnalytics({
	className,
}: AffiliateRewardsAnalyticsProps) {
	const { user, isAdmin } = useAuth();
	const navigate = useNavigate();

	// Query real affiliate analytics data
	const affiliateAnalytics = useQuery(
		api.affiliate.getAffiliateAnalytics,
		user ? { userId: user._id as unknown as Id<'users'> } : 'skip'
	);

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access Affiliate
								Rewards Analytics.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const formatNumber = (num: number | undefined) => {
		if (num === undefined || num === null) return '0';
		return num.toLocaleString();
	};

	return (
		<div className={cn('space-y-6 my-20', className)}>
			{/* Header with Back Button */}
			<div>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => navigate('/admin/analytics')}
					className='hover:bg-yellow-500/10'
				>
					<ArrowLeft className='h-4 w-4 mr-2' />
					Back to Analytics
				</Button>
			</div>
			<div className='flex items-center justify-between'>
				<div className='flex items-center gap-4'>
					<div>
						<h1 className='text-3xl font-bold text-white flex items-center gap-3'>
							<Target className='h-8 w-8 text-pink-500' />
							Affiliate Rewards
						</h1>
						<p className='text-muted-foreground mt-2'>
							Monitor affiliate performance, conversions, and
							earnings
						</p>
					</div>
				</div>
				<Badge
					variant='outline'
					className='bg-gradient-to-r from-pink-500 to-purple-600 text-white border-0 px-4 py-2'
				>
					Real-time Analytics
				</Badge>
			</div>

			{/* Affiliate Rewards Summary */}
			{affiliateAnalytics && (
				<Card>
					<CardHeader>
						<CardTitle className='flex items-center gap-2'>
							<Target className='h-5 w-5' />
							Affiliate Rewards Summary
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
							<div className='text-center p-4 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg'>
								<div className='text-2xl font-bold text-yellow-500'>
									{formatNumber(
										affiliateAnalytics.totalRewards
									)}
								</div>
								<div className='text-sm text-muted-foreground'>
									Total KARMA Earned
								</div>
							</div>
							<div className='text-center p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-lg'>
								<div className='text-2xl font-bold text-orange-500'>
									{formatNumber(
										affiliateAnalytics.pendingRewards
									)}
								</div>
								<div className='text-sm text-muted-foreground'>
									Pending KARMA
								</div>
							</div>
							<div className='text-center p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg'>
								<div className='text-2xl font-bold text-green-500'>
									{formatNumber(
										affiliateAnalytics.emailLeads
									)}
								</div>
								<div className='text-sm text-muted-foreground'>
									LEADS
								</div>
							</div>
							<div className='text-center p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg'>
								<div className='text-2xl font-bold text-purple-500'>
									{formatNumber(
										affiliateAnalytics.userSignups
									)}
								</div>
								<div className='text-sm text-muted-foreground'>
									SALES
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Overview Stats - NFT Metadata Aligned */}
			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6'>
				<Card className='bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20'>
					<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
						<CardTitle className='text-sm font-medium flex items-center gap-2'>
							<Eye className='h-4 w-4 text-green-500' />
							VIEWS
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='text-2xl font-bold text-green-500'>
							{affiliateAnalytics ? (
								formatNumber(affiliateAnalytics.totalVisits)
							) : (
								<Skeleton className='h-8 w-16' />
							)}
						</div>
						<p className='text-xs text-muted-foreground'>
							Total Unique Views
						</p>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-500/20'>
					<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
						<CardTitle className='text-sm font-medium flex items-center gap-2'>
							<Mail className='h-4 w-4 text-emerald-500' />
							LEADS
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='text-2xl font-bold text-emerald-500'>
							{affiliateAnalytics ? (
								formatNumber(affiliateAnalytics.emailLeads)
							) : (
								<Skeleton className='h-8 w-16' />
							)}
						</div>
						<p className='text-xs text-muted-foreground'>
							Email Subscriptions
						</p>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-pink-500/10 to-purple-500/10 border-pink-500/20'>
					<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
						<CardTitle className='text-sm font-medium flex items-center gap-2'>
							<Target className='h-4 w-4 text-pink-500' />
							SALES
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='text-2xl font-bold text-pink-500'>
							{affiliateAnalytics ? (
								formatNumber(affiliateAnalytics.userSignups)
							) : (
								<Skeleton className='h-8 w-16' />
							)}
						</div>
						<p className='text-xs text-muted-foreground'>
							New Watchers
						</p>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-500/20'>
					<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
						<CardTitle className='text-sm font-medium flex items-center gap-2'>
							<Activity className='h-4 w-4 text-blue-500' />
							CR
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='text-2xl font-bold text-blue-500'>
							{affiliateAnalytics ? (
								`${affiliateAnalytics.conversionRate.toFixed(1)}%`
							) : (
								<Skeleton className='h-8 w-16' />
							)}
						</div>
						<p className='text-xs text-muted-foreground'>
							Conversion Rate
						</p>
					</CardContent>
				</Card>

				<Card className='bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/20'>
					<CardHeader className='flex flex-row items-center space-y-0 pb-2'>
						<CardTitle className='text-sm font-medium flex items-center gap-2'>
							<TrendingUp className='h-4 w-4 text-yellow-500' />
							LEVEL
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='text-2xl font-bold text-yellow-500'>
							{affiliateAnalytics ? (
								// Calculate performance tier based on user signups and conversion rate
								(() => {
									const userSignups =
										affiliateAnalytics.userSignups;
									const conversionRate =
										affiliateAnalytics.conversionRate;
									if (
										userSignups >= 100 &&
										conversionRate >= 15
									)
										return 'Platinum';
									if (
										userSignups >= 50 &&
										conversionRate >= 10
									)
										return 'Gold';
									if (
										userSignups >= 20 &&
										conversionRate >= 5
									)
										return 'Silver';
									if (userSignups >= 5) return 'Bronze';
									return 'Starter';
								})()
							) : (
								<Skeleton className='h-8 w-16' />
							)}
						</div>
						<p className='text-xs text-muted-foreground'>
							Performance Tier
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Traffic Sources & Campaign Analytics */}
			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
				<Card>
					<CardHeader>
						<CardTitle className='flex items-center gap-2'>
							<Globe className='h-5 w-5' />
							Traffic Sources
						</CardTitle>
					</CardHeader>
					<CardContent>
						{affiliateAnalytics?.sourceBreakdown &&
						affiliateAnalytics.sourceBreakdown.length > 0 ? (
							<div className='space-y-4'>
								{affiliateAnalytics.sourceBreakdown
									.slice(0, 5)
									.map(
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										(source: any, index: number) => (
											<div
												key={index}
												className='flex items-center justify-between'
											>
												<div className='flex items-center gap-2'>
													<div className='h-3 w-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500' />
													<span className='capitalize'>
														{source.source}
													</span>
												</div>
												<div className='flex items-center gap-4'>
													<Badge variant='secondary'>
														{source.visits} visits
													</Badge>
													<Badge variant='outline'>
														{source.conversions}{' '}
														conversions
													</Badge>
													<span className='text-sm text-muted-foreground'>
														{source.conversionRate.toFixed(
															1
														)}
														%
													</span>
												</div>
											</div>
										)
									)}
							</div>
						) : (
							<div className='text-center text-muted-foreground py-8'>
								No traffic source data available yet
							</div>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className='flex items-center gap-2'>
							<TrendingUp className='h-5 w-5' />
							Campaign Performance
						</CardTitle>
					</CardHeader>
					<CardContent>
						{affiliateAnalytics?.campaignPerformance &&
						affiliateAnalytics.campaignPerformance.length > 0 ? (
							<div className='space-y-4'>
								{affiliateAnalytics.campaignPerformance
									.slice(0, 5)
									.map(
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										(campaign: any, index: number) => (
											<div
												key={index}
												className='flex items-center justify-between'
											>
												<div>
													<span className='font-medium'>
														{campaign.campaign}
													</span>
													<div className='text-xs text-muted-foreground'>
														Sources:{' '}
														{campaign.sources.join(
															', '
														)}
													</div>
												</div>
												<div className='flex items-center gap-4'>
													<Badge variant='secondary'>
														{campaign.visits} visits
													</Badge>
													<Badge variant='outline'>
														{campaign.conversions}{' '}
														conversions
													</Badge>
													<span className='text-sm text-muted-foreground'>
														{campaign.conversionRate.toFixed(
															1
														)}
														%
													</span>
												</div>
											</div>
										)
									)}
							</div>
						) : (
							<div className='text-center text-muted-foreground py-8'>
								No campaign data available yet
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* LEADS and SALES Cards */}
			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
				{/* LEADS Card */}
				<EmailLeadsCard
					affiliateAnalytics={affiliateAnalytics}
					formatNumber={formatNumber}
				/>

				{/* SALES Card */}
				<SalesCard
					affiliateAnalytics={affiliateAnalytics}
					formatNumber={formatNumber}
				/>
			</div>

			{/* Recent Affiliate Conversions with Pagination */}
			{affiliateAnalytics?.recentConversions &&
				affiliateAnalytics.recentConversions.length > 0 && (
					<RecentConversionsCard
						affiliateAnalytics={affiliateAnalytics}
						formatNumber={formatNumber}
					/>
				)}

			{/* Info Card */}
			<Card className='bg-gradient-to-br from-pink-500/5 to-purple-500/5 border-pink-500/20'>
				<CardContent className='p-6'>
					<div className='flex items-start gap-4'>
						<div className='p-3 bg-pink-500/10 rounded-xl'>
							<Target className='h-6 w-6 text-pink-500' />
						</div>
						<div className='flex-1'>
							<h3 className='font-semibold mb-2'>
								About Affiliate Rewards
							</h3>
							<p className='text-sm text-muted-foreground mb-3'>
								Earn KARMA tokens by referring new users to the
								platform. Track your performance, monitor
								conversions, and optimize your campaigns for
								maximum rewards.
							</p>
							<div className='flex gap-4 text-sm'>
								<div>
									<span className='text-muted-foreground'>
										Lead Reward:
									</span>
									<span className='ml-2 font-medium'>
										10 KARMA
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Sale Reward:
									</span>
									<span className='ml-2 font-medium'>
										100 KARMA
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Tier Bonus:
									</span>
									<span className='ml-2 font-medium text-pink-500'>
										Up to 2x
									</span>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
